import { Button, Checkbox, FormControlLabel, Grid, Slider } from '@mui/material';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import './Stat.scss';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import _ from 'lodash';
import PortSelector from '../PortSelector/PortSelector';
import { setComponentType, setCustomSizeMax, setCustomSizeMin, setEndDate, setIncludeFixed, setIncludeOutdated, setMasterAreas, setSelectAllPosition, setSelectAllSize, setSelectManualSize, setSizes, setStartDate, statSelector } from './reducers/StatReducer';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import Utility from '../../app/utility';
import StatApi, { StatResponse } from './api/StatApi';
import StatModel from './models/StatModel';
import BaseResponse from '../../app/BaseResponse';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Chart,
    ScatterDataPoint,
} from 'chart.js';
import { Bar, getDatasetAtEvent, getElementAtEvent } from 'react-chartjs-2';
import "chartjs-plugin-datalabels";
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const options = {
    responsive: true,
    plugins: {
        legend: {
            position: 'bottom' as const,
            labels: {
                padding: 10 //default is 10
            },
        },
        title: {
            display: true,
        },
        datalabels: {
            color: 'black',
            align: 'top' as const,
            anchor: 'end' as const,
            display: function (context: Context) {
                let data = context!.dataset!.data![context!.dataIndex] as number;
                return data! > 0;
            },
            font: {
                size: 16,
                weight: 'bold' as const
            }
        }
    },
    aspectRatio: 3
};

const tonnageColors = [
    "rgba(80, 117, 6)",
    "rgba(61, 201, 10)",
    "rgba(25, 176, 52)",
    "rgba(27, 179, 100)",
    "rgba(41, 194, 166)",
    "rgba(24, 158, 134)",
    "rgba(6, 151, 156)",
    "rgba(59, 158, 219)",
    "rgba(37, 85, 196)",
    "rgba(33, 13, 161)",
];

const cargoColors = [
    "rgb(255,171,0)",
    "rgb(255,127,0)",
    "rgb(255,56,1)",
    "rgb(211,1,76)",
    "rgb(168,0,109)",
];

const sizes = ["MINI", "HANDYSIZE", "HMX", "SMX", "PMX"];

const Tonnage: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const portData = useSelector(portSelectorSelector);
    const statData = useSelector(statSelector);
    const contactData = useSelector(contactInputSelector);
    const [chartData, setChartData] = useState<any>();

    useEffect(() => {
        document.title = "Stat";
    }, []);

    const refreshChart = () => {
        try {
            validate();

            StatApi.GetChart(statData.statInput)
                .then((response: BaseResponse<StatResponse>) => {
                    if (response.success) {
                        mapChartData(response.data);
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (statData.statInput.masterAreas.length === 0) {
            throw "Position is required";
        }

        if (statData.statInput.sizes.length === 0 &&
            statData.statInput.customSizeMin == 0 &&
            statData.statInput.customSizeMax == 0) {
            throw "Size is required";
        }

        if (statData.statInput.startDate.length === 0) {
            throw "Date 1 is required";
        }

        if (statData.statInput.endDate.length === 0) {
            throw "Date 2 is required";
        }

        return false;
    }

    const mapChartData = (data: StatResponse) => {
        let labels = [...statData.statInput.masterAreas];

        let tonnageDataSets: any[] = [];
        if (data.tonnageStats && data.tonnageStats.length > 0) {
            let index = -1;
            tonnageDataSets = _.map(statData.statInput.sizes, (size) => {
                index++;

                return {
                    label: size,
                    data: labels.map((masterArae) => {
                        let selectedChartData = _.find(data.tonnageStats, (statModel) => {
                            return statModel.masterArea === masterArae && statModel.size === size;
                        });

                        return selectedChartData ? selectedChartData.count : 0;
                    }),
                    backgroundColor: tonnageColors[index],
                };
            })

            if (statData.statInput.sizes.length === 0) {
                tonnageDataSets = [{
                    label: "Tonnage",
                    data: labels.map((masterArae) => {
                        let selectedChartData = _.find(data.tonnageStats, (statModel) => {
                            return statModel.masterArea === masterArae;
                        });

                        return selectedChartData ? selectedChartData.count : 0;
                    }),
                    backgroundColor: tonnageColors[0],
                }]
            }
        }

        let cargoDataSets: any[] = [];
        if (data.cargoStats && data.cargoStats.length > 0) {
            let index = -1;
            cargoDataSets = _.map(statData.statInput.sizes, (size) => {
                index++;

                return {
                    label: size,
                    data: labels.map((masterArae) => {
                        let selectedChartData = _.find(data.cargoStats, (statModel) => {
                            return statModel.masterArea === masterArae && statModel.size === size;
                        });

                        return selectedChartData ? selectedChartData.count : 0;
                    }),
                    backgroundColor: cargoColors[index],
                };
            })

            if (statData.statInput.sizes.length === 0) {
                cargoDataSets = [{
                    label: "Cargo",
                    data: labels.map((masterArae) => {
                        let selectedChartData = _.find(data.cargoStats, (statModel) => {
                            return statModel.masterArea === masterArae;
                        });

                        return selectedChartData ? selectedChartData.count : 0;
                    }),
                    backgroundColor: cargoColors[0],
                }]
            }
        }

        let dataSets = [...tonnageDataSets, ...cargoDataSets];

        const chartDataConfig = {
            labels,
            datasets: dataSets
        };

        setChartData(chartDataConfig);
    }

    return (
        <Grid container className="stat">
            <PortSelector
                areaLabel='Area'
                isAreaDisabled={false}
                hidePort={true}
                hideArea={true}
                freeSolo={true}
            >
            </PortSelector>
            <CustomAccordion
                title="Filter"
                defaultOpen={true}
                className="top-quick-input"
            >
                <Grid container>
                    <Grid item lg={7} xs={12} className="quick-input-component">
                        <Grid container>
                            <Grid xs={2} md={1} className="stat-filter-label">
                                Position
                            </Grid>
                            <Grid xs={10} md={11}>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={statData.selectAllPosition}
                                        onChange={(event) => {
                                            if (event.currentTarget.checked) {
                                                dispatch(setSelectAllPosition(true));
                                                dispatch(setMasterAreas([...portData.masterAreas]));
                                            } else {
                                                dispatch(setSelectAllPosition(false));
                                            }
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="All"
                                />
                                {
                                    portData.masterAreas &&
                                    _.map(portData.masterAreas, (masterArea) => {
                                        return <>
                                            <FormControlLabel control={
                                                <Checkbox
                                                    checked={_.indexOf(statData.statInput.masterAreas, masterArea) > -1}
                                                    onChange={(event) => {
                                                        let seletedMasterAreas = [...statData.statInput.masterAreas];
                                                        if (event.currentTarget.checked) {
                                                            seletedMasterAreas.push(masterArea);
                                                        } else {
                                                            dispatch(setSelectAllPosition(false));

                                                            let selectedIndex = _.indexOf(seletedMasterAreas, masterArea);
                                                            seletedMasterAreas.splice(selectedIndex, 1);
                                                        }
                                                        dispatch(setMasterAreas(seletedMasterAreas));
                                                    }}
                                                />}
                                                className="tonnage-input-grabs"
                                                label={masterArea}
                                            />
                                        </>
                                    })
                                }
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid xs={2} md={1} className="stat-filter-label">
                                Size
                            </Grid>
                            <Grid xs={10} md={11}>
                                <div>
                                    <FormControlLabel control={
                                        <Checkbox
                                            checked={statData.selectAllSize}
                                            onChange={(event) => {
                                                if (event.currentTarget.checked) {
                                                    dispatch(setSelectAllSize(true));
                                                    dispatch(setSizes([...sizes]));
                                                } else {
                                                    dispatch(setSelectAllSize(false));
                                                }
                                                dispatch(setSelectManualSize(false));
                                            }}
                                        />}
                                        className="tonnage-input-grabs"
                                        label="All"
                                    />
                                    {
                                        _.map(sizes, (size) => {
                                            return <>
                                                <FormControlLabel control={
                                                    <Checkbox
                                                        checked={_.indexOf(statData.statInput.sizes, size) > -1}
                                                        onChange={(event) => {
                                                            let seletedSizes = [...statData.statInput.sizes];
                                                            if (event.currentTarget.checked) {
                                                                seletedSizes.push(size);
                                                            } else {
                                                                dispatch(setSelectAllSize(false));

                                                                let selectedIndex = _.indexOf(seletedSizes, size);
                                                                seletedSizes.splice(selectedIndex, 1);
                                                            }
                                                            dispatch(setSizes(seletedSizes));
                                                            dispatch(setSelectManualSize(false));
                                                        }}
                                                    />}
                                                    className="tonnage-input-grabs"
                                                    label={size}
                                                />
                                            </>
                                        })
                                    }
                                </div>
                                <Grid container className='stat-manual-size'>
                                    <Grid xs={2}>
                                        <FormControlLabel control={
                                            <Checkbox
                                                checked={statData.selectManualSize}
                                                onChange={(event) => {
                                                    if (event.currentTarget.checked) {
                                                        dispatch(setSelectManualSize(true));
                                                        dispatch(setSelectAllSize(false));
                                                        dispatch(setSizes([]));
                                                    } else {
                                                        dispatch(setSelectManualSize(false));
                                                    }
                                                }}
                                            />}
                                            className="tonnage-input-grabs"
                                            label="Manual Size"
                                        />
                                    </Grid>
                                    <Grid xs={10} className="stat-filter-range">
                                        <Slider
                                            defaultValue={0} aria-label="Default" valueLabelDisplay="on"
                                            min={0}
                                            max={100}
                                            marks={[{
                                                value: 0,
                                                label: "0 Dwt"
                                            }, {
                                                value: 100,
                                                label: "100k Dwt"
                                            }]}
                                            onChange={(e, value: number | number[]) => {
                                                let selectedValue = value as number[];
                                                dispatch(setCustomSizeMin(selectedValue[0]));
                                                dispatch(setCustomSizeMax(selectedValue[1]));
                                            }}
                                            value={[statData.statInput.customSizeMin, statData.statInput.customSizeMax]}
                                            disabled={!statData.selectManualSize}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={4} xs={12} className="quick-input-component">
                        <Grid container className="tonnage-search-item">
                            <Grid item xs={4}>
                                <Button
                                    variant={statData.statInput.componentType === "Tonnage" ? "contained" : "outlined"}
                                    fullWidth
                                    onClick={() => {
                                        dispatch(setComponentType("Tonnage"));
                                    }}
                                >TONNAGE</Button>
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    variant={statData.statInput.componentType === "Cargo" ? "contained" : "outlined"}
                                    fullWidth
                                    onClick={() => {
                                        dispatch(setComponentType("Cargo"));
                                    }}
                                >CARGO</Button>
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    variant={statData.statInput.componentType === "Combine" ? "contained" : "outlined"}
                                    fullWidth
                                    onClick={() => {
                                        dispatch(setComponentType("Combine"));
                                    }}
                                >COMBINE</Button>
                            </Grid>
                        </Grid>
                        <StartEndDatepicker
                            startDateLabel={"Date 1"}
                            endDateLabel={"Date 2"}
                            className="tonnage-search-item"
                            useQuickEndDateInput={true}
                            startDateValue={statData.statInput.startDate}
                            endDateValue={statData.statInput.endDate}
                            onStartDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setStartDate(date));
                                }
                            }}
                            onEndDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setEndDate(date));
                                }
                            }}
                        >
                        </StartEndDatepicker>
                        <Grid container>
                            <Grid item xs={6}>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={statData.statInput.includeOutdated}
                                        onChange={(event) => {
                                            dispatch(setIncludeOutdated(event.currentTarget.checked));
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="Include outdated"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={statData.statInput.includeFixed}
                                        onChange={(event) => {
                                            dispatch(setIncludeFixed(event.currentTarget.checked));
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="Include fixed"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={1} xs={12} className="quick-input-component">
                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={() => {
                                refreshChart();
                            }}>
                            Refresh Chart
                        </Button>
                    </Grid>
                </Grid>
            </CustomAccordion>
            <Grid className="stat-chart">
                {
                    chartData != null &&
                    <Bar
                        options={options}
                        data={chartData}
                        plugins={[ChartDataLabels]}
                    />
                }
            </Grid>
        </Grid>
    )
};

export default Tonnage;